import { useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getCity } from '../data/locations';
import { pillClass } from './SiteHeader';

export function PhotoLightbox({ from }: { from: 'globe' | 'list' }) {
  const { slug, photoId } = useParams<{ slug: string; photoId: string }>();
  const navigate = useNavigate();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const touchX = useRef<number | null>(null);

  const city = getCity(slug);
  const index = city ? city.photos.findIndex((p) => p.id === photoId) : -1;

  // Open once. Never navigate from the dialog's own 'close' event — instead all
  // exits call close() which route-replaces (no Back-button loop).
  useLayoutEffect(() => {
    const dlg = dialogRef.current;
    if (dlg && !dlg.open) dlg.showModal();
    return () => {
      if (dlg?.open) dlg.close();
    };
  }, []);

  // Preload + decode adjacent full-size images so nav feels instant.
  useEffect(() => {
    if (!city || index < 0) return;
    const n = city.photos.length;
    for (const j of [(index + 1) % n, (index - 1 + n) % n]) {
      const im = new Image();
      im.src = city.photos[j].url;
      void im.decode?.().catch(() => {});
    }
  }, [city, index]);

  const n = city?.photos.length ?? 0;
  const photo = index >= 0 ? city?.photos[index] : undefined;
  const base = from === 'list' ? '/list' : city ? `/city/${city.id}` : '/';
  const close = () => navigate(base, { replace: true });
  const go = (d: number) => {
    if (!city) return;
    const next = city.photos[(index + d + n) % n];
    navigate(`${from === 'list' ? `/list/${city.id}` : `/city/${city.id}`}/photo/${next.id}`, { replace: true });
  };

  return (
    <dialog
      ref={dialogRef}
      aria-label={city && photo ? `${city.name} photographs` : 'Photo not found'}
      onCancel={(e) => {
        e.preventDefault();
        close();
      }}
      onKeyDown={(e) => {
        if (!photo) return;
        if (e.key === 'ArrowRight') go(1);
        if (e.key === 'ArrowLeft') go(-1);
      }}
      className="m-0 h-full max-h-none w-full max-w-none bg-transparent p-0 backdrop:bg-black/85 backdrop:backdrop-blur-sm"
    >
      <div
        className="relative flex h-[100dvh] w-full items-center justify-center p-4"
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) close();
        }}
      >
        {city && photo ? (
          <>
            {[
              { fn: close, label: 'Close', cls: 'right-4 top-4', d: 'M6 6l12 12M18 6L6 18' },
              { fn: () => go(-1), label: 'Previous', cls: 'left-3 top-1/2 -translate-y-1/2', d: 'M15 18l-6-6 6-6' },
              { fn: () => go(1), label: 'Next', cls: 'right-3 top-1/2 -translate-y-1/2', d: 'M9 6l6 6-6 6' },
            ].map((b) => (
              <button
                key={b.label}
                onClick={b.fn}
                aria-label={b.label}
                className={`absolute z-10 grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-black/55 text-white/85 transition hover:bg-black/75 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#f2c66d] focus-visible:outline-offset-2 ${b.cls}`}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d={b.d} strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            ))}

            <img
              key={photo.id}
              src={photo.url}
              alt={`${city.name}, photo ${index + 1} of ${n}`}
              decoding="async"
              onTouchStart={(e) => (touchX.current = e.touches[0].clientX)}
              onTouchEnd={(e) => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                touchX.current = null;
                if (Math.abs(dx) > 45) go(dx < 0 ? 1 : -1);
              }}
              style={{ animation: 'photoin .2s ease' }}
              className="max-h-[82dvh] max-w-full rounded-md object-contain shadow-2xl"
            />

            {/* editorial info bar */}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 bg-gradient-to-t from-black/70 to-transparent p-5 pb-[max(20px,env(safe-area-inset-bottom))]">
              <div>
                <p className="text-[15px] font-[550] text-white">
                  {city.name}, {city.country}
                </p>
                <p className="mt-0.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white/55">{city.date}</p>
              </div>
              <p className="font-mono text-[12px] tracking-[0.1em] text-white/70">
                {String(index + 1).padStart(2, '0')} / {n}
              </p>
            </div>
          </>
        ) : (
          <div className="max-w-[34ch] rounded-lg border border-white/10 bg-[#05070c] px-8 py-9 text-white">
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-white/55">Not found</p>
            <h2 className="mt-2 text-[28px] font-[560] leading-[1.1] tracking-tight">No photo here.</h2>
            <p className="mt-4 text-[15px] leading-[1.6] text-white/70">
              {city ? `${city.name} has no photo with this address.` : "This address doesn't match any place."}
            </p>
            <button type="button" onClick={close} className={`mt-6 ${pillClass}`}>
              {from === 'list' ? '← Back to the list' : city ? `← Back to ${city.name}` : '← Back to globe'}
            </button>
          </div>
        )}
      </div>
    </dialog>
  );
}
